import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Camera as CameraIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { createMemory, updateMemory, getUserMemoryByGame, getUserGameStatus, deleteMemory } from '../../services/api';
import StyledTextField from '../common/StyledTextField';
import ConfirmDialog from './ConfirmDialog';
import Tips from './Tips';
import ReviewPreviewDialog from './ReviewPreviewDialog';

export interface GameMemoryDialogProps {
  open: boolean;
  onClose: () => void;
  gameId: number;
  gameName: string;
  onMemoryCreated?: () => void;
}

const GameMemoryDialog: React.FC<GameMemoryDialogProps> = ({
  open,
  onClose,
  gameId,
  gameName,
  onMemoryCreated,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [memoryId, setMemoryId] = useState<number | null>(null);
  const [loadingMemory, setLoadingMemory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gameStatus, setGameStatus] = useState<{
    playStartDate: string | null;
    playEndDate: string | null;
    status: number | null;
  }>({ playStartDate: null, playEndDate: null, status: null });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // ダイアログが開いたらデータ取得
  useEffect(() => {
    if (open && gameId) {
      loadData();
    }
  }, [open, gameId]);

  const loadData = async () => {
    setLoadingMemory(true);
    try {
      const [statusResponse, existingMemory] = await Promise.allSettled([
        getUserGameStatus(gameId),
        getUserMemoryByGame(gameId)
      ]);

      if (statusResponse.status === 'fulfilled') {
        setGameStatus({
          playStartDate: statusResponse.value.playStartDate,
          playEndDate: statusResponse.value.playEndDate,
          status: statusResponse.value.status
        });
      }

      if (existingMemory.status === 'fulfilled' && existingMemory.value) {
        setIsEditMode(true);
        setMemoryId(existingMemory.value.id);
        setTitle(existingMemory.value.title);
        setContent(existingMemory.value.content);
        setIsPublic(existingMemory.value.is_public);
        setIsAnonymous(existingMemory.value.is_anonymous);
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('データの取得に失敗しました:', error);
      resetForm();
    } finally {
      setLoadingMemory(false);
    }
  };

  const resetForm = () => {
    setIsEditMode(false);
    setMemoryId(null);
    setTitle('');
    setContent('');
    setIsPublic(true);
    setIsAnonymous(false);
  };

  const handleClose = () => {
    resetForm();
    setDeleting(false);
    setOpenDeleteConfirmDialog(false);
    setPreviewOpen(false);
    setGameStatus({ playStartDate: null, playEndDate: null, status: null });
    onClose();
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setLoading(true);
    try {
      const memoryData = {
        title: title.trim(),
        content: content.trim(),
        is_public: isPublic,
        is_anonymous: isAnonymous,
      };

      if (isEditMode && memoryId) {
        await updateMemory(memoryId, memoryData);
      } else {
        await createMemory({ game: gameId, ...memoryData });
      }

      handleClose();
      onMemoryCreated?.();
    } catch (error) {
      console.error('メモリーの投稿に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteConfirmDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memoryId) return;

    setDeleting(true);
    try {
      await deleteMemory(memoryId);
      handleClose();
      onMemoryCreated?.();
    } catch (error) {
      console.error('メモリーの削除に失敗しました:', error);
    } finally {
      setDeleting(false);
      setOpenDeleteConfirmDialog(false);
    }
  };

  const getDisplayDate = () => {
    if (gameStatus.playEndDate) return gameStatus.playEndDate;
    if (gameStatus.playStartDate) return gameStatus.playStartDate;
    return null;
  };

  const getDateSourceText = () => {
    if (gameStatus.playEndDate) return 'プレイ終了日時';
    if (gameStatus.playStartDate) return 'プレイ開始日時';
    return null;
  };

  const reviewFormatTips = (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
          lineHeight: 1.2
        }}
      >
        Tips：コードをクリックでクリップボードにコピーできます
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          {
            title: '見出し',
            examples: [
              { syntax: '[h1]ヘッダー[/h1]', result: 'ヘッダー' },
              { syntax: '[h2]ヘッダー[/h2]', result: 'ヘッダー' },
            ]
          },
          {
            title: 'テキスト装飾',
            examples: [
              { syntax: '[b]太字[/b]', result: '太字' },
              { syntax: '[i]斜体[/i]', result: '斜体' },
              { syntax: '[s]取り消し線[/s]', result: '取り消し線' },
              { syntax: '[u]下線[/u]', result: '下線' }
            ]
          },
          {
            title: 'リスト',
            examples: [
              { syntax: '[list]\n[*]リスト項目\n[*]リスト項目\n[/list]', result: '箇条書き' },
              { syntax: '[olist]\n[*]リスト項目\n[*]リスト項目\n[/olist]', result: '番号付きリスト' }
            ]
          },
          {
            title: 'その他',
            examples: [
              { syntax: '[quote]引用文[/quote]', result: '引用ブロック' },
              { syntax: '[spoiler]ネタバレ[/spoiler]', result: 'ネタバレ' },
            ]
          }
        ].map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              {section.title}
            </Typography>
            {section.examples.map((example, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1,
                  mb: 0.5,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' }
                }}
              >
                <Box
                  component="code"
                  onClick={() => navigator.clipboard.writeText(example.syntax)}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    p: 0.5,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.4)' }
                  }}
                >
                  {example.syntax}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {example.result}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditMode ? <EditIcon /> : <CameraIcon />}
            <Typography variant="h6">
              {isEditMode ? 'メモリーを編集' : 'メモリーを投稿'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isEditMode && (
              <Tooltip title="削除" arrow>
                <IconButton
                  onClick={handleDeleteClick}
                  size="small"
                  disabled={deleting || loadingMemory}
                  sx={{
                    color: 'rgba(255, 99, 99, 0.7)',
                    '&:hover': {
                      color: 'rgba(255, 99, 99, 1)',
                      backgroundColor: 'rgba(255, 99, 99, 0.1)'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {gameName}
          </Typography>

          {loadingMemory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                データを読み込み中...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <StyledTextField
                  id="title"
                  label="タイトル（任意）"
                  size="small"
                  value={title}
                  onChange={setTitle}
                  placeholder="タイトルを入力..."
                  multiline={false}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: -1
              }}>
                <Button
                  color="primary"
                  size="small"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!content.trim()}
                  sx={{
                    fontSize: { xs: "0.55rem", sm: "0.6rem" },
                    minWidth: 'auto',
                    p: '2px 8px'
                  }}
                >
                  プレビュー
                </Button>
                <Tips
                  title="テキストフォーマット"
                  content={reviewFormatTips}
                  buttonComponent={
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.6rem" },
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      使用可能フォーマット
                    </Typography>
                  }
                />
              </Box>
              <Box sx={{ mt: 0.25, mb: 2 }}>
                <StyledTextField
                  id="content"
                  label="内容"
                  value={content}
                  onChange={setContent}
                  multiline
                  minRows={fullScreen ? 6 : 4}
                  maxRows={12}
                  placeholder="このゲームの思い出を書いてみましょう"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="プレイ時期"
                  type="date"
                  value={getDisplayDate() || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                    style: {
                      color: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)'
                    }
                  }}
                  size="small"
                  disabled
                  helperText={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <InfoIcon sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {getDateSourceText()
                          ? `ゲームステータスの${getDateSourceText()}が適用されます`
                          : 'ゲームステータスに日付が設定されていません'
                        }
                      </Typography>
                    </Box>
                  }
                />
                {!getDisplayDate() && gameStatus.status && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 1,
                      backgroundColor: 'rgba(33, 150, 243, 0.15)',
                      border: '1px solid rgba(33, 150, 243, 0.3)',
                      '& .MuiAlert-message': {
                        color: 'rgba(255, 255, 255, 0.8)'
                      }
                    }}
                  >
                    ゲームステータスでプレイ日時を設定すると、メモリーに反映されます
                  </Alert>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingMemory || deleting || !content.trim()}
          >
            {loading ? (isEditMode ? '更新中...' : '投稿中...') : (isEditMode ? '更新' : '投稿')}
          </Button>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={loading || loadingMemory || deleting}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteConfirmDialog}
        title="削除の確認"
        message="メモリーを削除しますか？削除すると元に戻すことはできません。"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirmDialog(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />

      <ReviewPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reviewText={content}
      />
    </>
  );
};

export default GameMemoryDialog;
